<script setup lang="ts">
import { computed, type ExtractPropTypes } from 'vue'
import { useVfm, VueFinalModal } from 'vue-final-modal'
import { v4 as uuid } from 'uuid'
import { omit } from 'lodash'
import { cva } from 'class-variance-authority'

defineOptions({ inheritAttrs: false })

const vfm = useVfm()

interface Props extends /* @vue-ignore */ ExtractPropTypes<typeof VueFinalModal> {
    modalId?: string
    size?: `sm` | `md` | `lg` | `full` | `custom`
    class?: string[] | string
}

const props = withDefaults(defineProps<Props>(), {
    modalId: () => uuid(),
    size: `sm`,
    class: ``
})

const open = () => {
    vfm.open(props.modalId)
}

const close = () => {
    vfm.close(props.modalId)
}

const closeAll = () => {
    vfm.closeAll()
}

const contentClasses = cva(`my-auto w-full rounded-lg bg-white shadow outline-none`, {
    variants: {
        size: {
            sm: `max-w-[20rem]`,
            md: `max-w-[40rem]`,
            lg: `max-w-[60rem]`,
            full: `max-w-none`,
            custom: ``
        }
    }
})

// KNOWN ISSUE (BRZE-5124): caller layout classes (props.class) are merged onto vfm__content,
// which is ALSO the element vue-final-modal transforms via contentTransition below. Any caller
// passing a flex `gap-*` here (~28 modals do) puts `gap` on a transform target, which triggers a
// WKWebView-only layout break (modal flashes on open, then collapses). SmsEditorModal works around
// this locally by using `space-y-4` instead of `gap-4`. Systemic fix: render an inner wrapper that
// receives props.class so the transitioned vfm__content stays a plain block — deferred (touches all
// modals, needs a visual pass). See kb: breeze-ios-wrapper-app/brze-5124-horizon-ui-gap-css-wkwebview.
const modalProps = computed(() => ({
    ...omit(props, [`size`]),
    class: `horizon-ui`,
    contentClass: [props.class, contentClasses({ size: props.size })],
    overlayClass: `backdrop-blur-[2px] fixed`,
    overlayTransition: {
        enterActiveClass: `transition-opacity duration-200`,
        leaveActiveClass: `transition-opacity duration-200`,
        enterFromClass: `opacity-0`,
        leaveToClass: `opacity-0`
    },
    contentTransition: {
        enterActiveClass: `transition-all duration-300`,
        leaveActiveClass: `transition-all duration-300`,
        enterFromClass: `opacity-0 -translate-y-8`,
        leaveToClass: `opacity-0 translate-y-8`
    },
    style: {
        display: `flex`,
        alignItems: `center`,
        justifyContent: `center`,
        overflowY: `auto`
    }
}))

defineExpose({ open, close, closeAll })
</script>

<template>
    <VueFinalModal v-bind="{ ...modalProps, ...$attrs }">
        <slot />
    </VueFinalModal>
</template>
